<template>
  <!-- MBR059 Donation — member_app_UI/song_fa_members_donation_mobile -->
  <div class="page-header align-items-start min-vh-100 mbr-donate">
    <span class="mask bg-white"></span>
    <div class="container p-0">
      <!-- MBR060 header: back + title, History still opens My Transactions -->
      <header class="mbr-donate__bar">
        <div class="mbr-donate__head">
          <button type="button" class="mbr-donate__back" :aria-label="$t('common.back')" @click="goBack()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <h1 class="mbr-donate__title">{{ $t('donation.title') }}</h1>
        </div>
        <button type="button" class="mbr-donate__history" @click="gotoTransaction()">{{ $t('donation.history') }}</button>
      </header>

      <main class="mbr-donate__body pb-90">
        <!-- MBR060 intro card (merchant-neutral copy) -->
        <section class="mbr-donate__intro">
          <span class="mbr-donate__intro-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </span>
          <!-- <p>, not <h2>: Material Dashboard sizes headings -->
          <p class="mbr-donate__intro-title">{{ $t('donation.intro_title') }}</p>
          <p class="mbr-donate__intro-text">
            {{ $t('donation.intro_text') }}
          </p>
        </section>

        <!-- MBR060 amount stepper -->
        <section class="mbr-donate__amount">
          <label for="donateAmount" class="mbr-donate__eyebrow">{{ $t('donation.enter_amount') }}</label>
          <div class="mbr-donate__stepper">
            <button
              type="button"
              class="mbr-donate__step"
              :aria-label="$t('donation.decrease_amount')"
              :disabled="!amountValue"
              @click="upDownAmount()"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                <path stroke-linecap="round" d="M5 12h14" />
              </svg>
            </button>
            <div class="mbr-donate__value" @click="focusAmount">
              <span class="mbr-donate__currency">$</span>
              <input
                ref="amountInput"
                id="donateAmount"
                name="donateAmount"
                class="mbr-donate__input"
                type="number"
                inputmode="decimal"
                min="0"
                placeholder="0"
                :style="{ width: amountWidth }"
                v-model.number="donateAmount"
                @input="validateDonateAmount"
                @keydown.up.prevent
                @keydown.down.prevent
              />
            </div>
            <button
              type="button"
              class="mbr-donate__step mbr-donate__step--plus"
              :aria-label="$t('donation.increase_amount')"
              @click="upDownAmount(1)"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                <path stroke-linecap="round" d="M12 5v14M5 12h14" />
              </svg>
            </button>
          </div>
          <div class="mbr-donate__chips">
            <button
              v-for="chip in quickAmounts"
              :key="chip"
              type="button"
              class="mbr-donate__chip"
              :class="{ 'mbr-donate__chip--on': amountValue === chip }"
              :aria-pressed="amountValue === chip ? 'true' : 'false'"
              @click="donateAmount = chip"
            >
              $ {{ chip }}
            </button>
          </div>
        </section>

        <!-- MBR060 Terms & Conditions accordion, closed by default -->
        <section v-if="terms_conditions" class="mbr-donate__terms">
          <button
            type="button"
            class="mbr-donate__terms-toggle"
            :aria-expanded="showTerms ? 'true' : 'false'"
            aria-controls="mbr-donate-terms"
            @click="showTerms = !showTerms"
          >
            <span>{{ $t('donation.terms') }}</span>
            <svg
              :class="{ 'mbr-donate__chev--open': showTerms }"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7" />
            </svg>
          </button>
          <div
            v-show="showTerms"
            id="mbr-donate-terms"
            class="mbr-donate__terms-content"
            v-html="terms_conditions"
          ></div>
        </section>
      </main>
    </div>

    <!-- MBR060 sticky CTA -->
    <div class="mbr-donate__ctabar">
      <div class="mbr-donate__ctabar-inner">
        <button
          type="button"
          class="mbr-donate__cta"
          :disabled="amountValue <= 0"
          @click="confirmDonate"
        >
          <span>{{ $t('donation.proceed_to_payment') }}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14m-6-6 6 6-6 6" />
          </svg>
        </button>
      </div>
    </div>

    <div class="position-fixed top-1 end-1 z-index-2">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="loaderActive" />
    <ModalConfirmDonate
      :amount="amountValue"
      :btnYes="$t('common.yes')"
      :sycn-profile="processPayment"
    />
    <DonateCompleted :amount="String(currentAmount)" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import { mapMutations, mapActions } from "vuex";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import StripeService from "../services/stripe.service";
import HomeService from "../services/home.service";
import MemberService from "../services/member.service";
import "bootstrap/dist/js/bootstrap.bundle";
import ModalConfirmDonate from "@/components/ModalConfirmDonate.vue";
import DonateCompleted from "@/components/DonateCompleted.vue";
import $ from "jquery";
import { localStorageName, getMerchantCode } from "@/config/utils";
import { errorMessage } from "@/utils/errorMessage";

export default {
  name: "donation",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    DonateCompleted,
    ModalConfirmDonate
  },
  data() {
    return {
      prices: [],
      merchantCode: "",
      donateAmount: null,
      currentAmount: 0,
      loaderActive: false,
      snackbar: "",
      message: "",
      terms_conditions: "",
      quickAmounts: [5, 10, 20, 50],
      showTerms: false,
      memberInfo: {},
    };
  },
  created() {
    this.getDonationTermsConditions();
    this.getMemberInfo();
  },
  // draws its own header like the design: hide the top navbar, and keep the
  // sidenav as an off-screen drawer (same as Topup)
  beforeMount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.add("mbr-sidenav-overlay");
    app.classList.remove("g-sidenav-pinned");
  },
  beforeUnmount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.remove("mbr-sidenav-overlay");
    if (window.innerWidth > 1200) {
      app.classList.add("g-sidenav-pinned");
    }
  },
  mounted() {
    const code = getMerchantCode();
    const isCheckMerchantConnectedStripe = localStorage.getItem(
      localStorageName("IS_CHECK_MERCHANT_CONNECTED_STRIPE"),
      "false"
    );
    this.merchantCode = code ? `/${code}` : "";

    if (isCheckMerchantConnectedStripe === "false") {
      window.location.href = this.merchantCode + "/home";
    }
    //load after payment done
    var session_id = this.$route.query.session_id;
    var amount = this.$route.query.price;

    if (session_id) {
      // call back after pay success - stripe
      this.showLoader();
      var dataForm = {
        session_id: session_id,
        amount: amount
      }

      StripeService.saveDonate(dataForm, this.merchantCode).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.currentAmount = amount;
            $(".btn-donate_completed_modal").click();
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    }
  },
  computed: {
    // the input clears to "" — treat anything non-numeric as 0
    amountValue() {
      return Number(this.donateAmount) || 0;
    },
    // grow the input with its digits so "S$ 10" stays centred
    amountWidth() {
      const length = String(this.donateAmount ?? "").length || 1;
      return `${length + 0.2}ch`;
    },
  },
  methods: {
    ...mapMutations(["toggleNavbarOnly", "toggleHideConfig"]),
    ...mapActions(["setHeaderColor", "setLabelColor", "setButtonColor"]),
    validateDonateAmount(event) {
      var amount = parseFloat(event.target.value);

      if (amount < 0) {
        this.donateAmount = 0;
      }
    },
    focusAmount() {
      this.$refs.amountInput.focus();
    },
    goBack() {
      this.$router.push(this.merchantCode + "/account");
    },
    upDownAmount(type = 0) {
      if (type) {
        this.donateAmount = this.amountValue + 1;
      } else {
        if (this.amountValue > 1) {
          this.donateAmount = this.amountValue - 1;
        } else {
          this.donateAmount = 0;
        }
      }
    },
    confirmDonate() {
      $('.btn-confirm-modal-donate').click();
    },
    getDonationTermsConditions() {
      this.showLoader();
      HomeService.getDonationTermsConditions().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.terms_conditions = response.data.data;
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.setTimeOutNotification();
          this.hideLoader();
        }
      )
    },
    processPayment() {
      this.showLoader();
      const merchantCode = getMerchantCode();
      var url =
        location.protocol +
        "//" +
        location.host +
        "/" +
        merchantCode +
        "/donation";
      var payment_amount = new Intl.NumberFormat("en", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(this.amountValue);
      var dataSend = {
        success_url:
          url +
          "?session_id={CHECKOUT_SESSION_ID}&price=" +
          payment_amount,
        cancel_url: url,
        images: "",
        name: "Donate $" + payment_amount,
        unit_amount: payment_amount,
        quantity: 1,
        member_id: this.memberInfo.id,
      };

      StripeService.checkoutSessionStripe(dataSend, merchantCode).then(
        (response) => {
          if (response.data.result.isSuccess) {
            window.location.href = response.data.data.url;
          } else {
            this.closeConfirm();
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
            this.hideLoader();
          }
        },
        (error) => {
          this.closeConfirm();
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
    // the sheet sits above the loader/snackbar, so drop it before reporting an error
    closeConfirm() {
      $("#bottom_modal_confirm_donate .close-model").click();
    },
    getMemberInfo() {
      MemberService.getProfile().then(
        (response) => {
          this.memberInfo = response.data.data;
        },
        (error) => {
          console.log(error);
        }
      );
    },
    gotoTransaction() {
      this.$router.push(this.merchantCode + "/my-transactions");
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
  },
};
</script>
<style scoped>
/* MBR059 Donation */
.mbr-donate.page-header {
  overflow: visible;
  overflow-x: clip;
  font-family: var(--mbr-font-body);
  color: var(--mbr-color-ink-headline);
}

/* custom.css still carries the legacy `* { font-family: Poppins }` */
.mbr-donate :where(:not(.material-icons-round)) {
  font-family: var(--mbr-font-body);
}

.mbr-donate .mask {
  background: var(--mbr-color-canvas) !important;
  opacity: 1;
}

.mbr-donate .container {
  max-width: 440px;
  margin: 0 auto;
}

/* MBR060 header */
.mbr-donate__bar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 24px 20px 12px;
  background: var(--mbr-nav-bg);
  border-bottom: 1px solid rgba(var(--mbr-color-outline-rgb), 0.4);
  backdrop-filter: blur(var(--mbr-nav-blur));
  -webkit-backdrop-filter: blur(var(--mbr-nav-blur));
}

.mbr-donate__head {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.mbr-donate__back {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: var(--mbr-color-surface);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  color: var(--mbr-color-primary);
  transition: transform 0.15s ease;
}

.mbr-donate__back:active {
  transform: scale(0.95);
}

.mbr-donate__back svg {
  width: 16px;
  height: 16px;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-donate__title {
  margin: 0;
  font-family: var(--mbr-font-headline) !important;
  font-size: 20px;
  font-weight: 800;
  line-height: 28px;
  letter-spacing: -0.02em;
  color: var(--mbr-color-ink-strong);
}

.mbr-donate__history {
  padding: 4px 0;
  background: none;
  border: 0;
  font-size: 12px;
  font-weight: 700;
  color: var(--mbr-color-primary);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}

.mbr-donate__back:focus-visible,
.mbr-donate__history:focus-visible,
.mbr-donate__step:focus-visible,
.mbr-donate__chip:focus-visible,
.mbr-donate__terms-toggle:focus-visible,
.mbr-donate__cta:focus-visible {
  outline: 2px solid var(--mbr-color-primary);
  outline-offset: 2px;
}

.mbr-donate__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 20px 0;
}

/* MBR060 intro card */
.mbr-donate__intro {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: rgba(var(--mbr-color-palepink-rgb), 0.4);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.7);
  border-radius: 24px;
  text-align: center;
}

.mbr-donate__intro-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 12px;
  border-radius: var(--mbr-radius-full);
  background: var(--mbr-color-peach);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  color: var(--mbr-color-primary);
}

.mbr-donate__intro-icon svg {
  width: 32px;
  height: 32px;
}

.mbr-donate__intro-title {
  margin: 0 0 4px;
  font-family: var(--mbr-font-headline) !important;
  font-size: 20px;
  font-weight: 800;
  line-height: 28px;
  color: var(--mbr-color-ink-strong);
}

.mbr-donate__intro-text {
  max-width: 280px;
  margin: 0;
  font-size: 12px;
  line-height: 19px;
  color: var(--mbr-color-ink-secondary);
}

/* MBR060 amount card */
.mbr-donate__amount {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: var(--mbr-color-surface);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
  border-radius: 24px;
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-donate__eyebrow {
  display: block;
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0.05em;
  text-align: center;
  text-transform: uppercase;
  color: var(--mbr-color-ink-secondary);
}

.mbr-donate__stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 8px 0;
}

.mbr-donate__step {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  background: var(--mbr-color-surface);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  color: var(--mbr-color-primary);
  transition: transform 0.15s ease;
}

.mbr-donate__step--plus {
  background: var(--mbr-color-primary);
  border-color: var(--mbr-color-primary);
  box-shadow: 0 4px 10px -2px rgba(var(--mbr-color-primary-rgb), 0.3);
  color: var(--mbr-color-on-primary);
}

.mbr-donate__step:active:not(:disabled) {
  transform: scale(0.95);
}

.mbr-donate__step:disabled {
  opacity: 0.4;
}

.mbr-donate__step svg {
  width: 20px;
  height: 20px;
}

.mbr-donate__value {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
  min-width: 140px;
  cursor: text;
}

.mbr-donate__currency,
.mbr-donate__input {
  font-family: var(--mbr-font-headline) !important;
  font-size: 40px;
  font-weight: 800;
  line-height: 48px;
  letter-spacing: -0.02em;
  color: var(--mbr-color-ink-strong);
}

.mbr-donate__input {
  min-width: 1.2ch;
  max-width: 5.5ch;
  padding: 0;
  background: transparent;
  border: 0;
  border-bottom: 2px solid transparent;
  outline: none;
  text-align: left;
  -moz-appearance: textfield;
}

.mbr-donate__input:focus {
  border-bottom-color: var(--mbr-color-outline);
}

/* the global input::placeholder rule shrinks it otherwise */
.mbr-donate__input::placeholder {
  font-size: inherit;
  font-weight: inherit;
  color: var(--mbr-color-ink-muted);
  opacity: 1;
}

.mbr-donate__input::-webkit-outer-spin-button,
.mbr-donate__input::-webkit-inner-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

.mbr-donate__chips {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  padding-top: 8px;
}

.mbr-donate__chip {
  padding: 8px 0;
  background: var(--mbr-color-canvas);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: var(--mbr-color-ink-secondary);
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.mbr-donate__chip:hover {
  background: var(--mbr-color-tint);
  border-color: var(--mbr-color-primary);
}

.mbr-donate__chip--on,
.mbr-donate__chip--on:hover {
  background: var(--mbr-color-primary);
  border-color: var(--mbr-color-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.15);
  color: var(--mbr-color-on-primary);
}

/* MBR060 T&C accordion */
.mbr-donate__terms {
  overflow: hidden;
  background: var(--mbr-color-card);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.7);
  border-radius: var(--mbr-radius-lg);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-donate__terms-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  background: none;
  border: 0;
  text-align: left;
}

.mbr-donate__terms-toggle span {
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 700;
  color: var(--mbr-color-ink-headline);
}

.mbr-donate__terms-toggle svg {
  width: 16px;
  height: 16px;
  color: var(--mbr-color-ink-secondary);
  transition: transform 0.2s ease;
}

.mbr-donate__terms-toggle svg.mbr-donate__chev--open {
  transform: rotate(180deg);
}

.mbr-donate__terms-content {
  padding: 10px 16px 14px;
  border-top: 1px solid rgba(var(--mbr-color-outline-rgb), 0.4);
  font-size: 11px;
  line-height: 18px;
  color: rgba(var(--mbr-color-ink-secondary-rgb), 0.8);
  overflow-wrap: anywhere;
}

/* merchant HTML: Material Dashboard sizes <p> at 1rem */
.mbr-donate__terms-content :deep(p) {
  margin-bottom: 8px;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
}

.mbr-donate__terms-content :deep(p:last-child) {
  margin-bottom: 0;
}

/* MBR060 sticky CTA */
.mbr-donate__ctabar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1030;
  background: rgba(var(--mbr-color-canvas-rgb), 0.95);
  border-top: 1px solid rgba(var(--mbr-color-outline-rgb), 0.8);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.mbr-donate__ctabar-inner {
  max-width: 440px;
  margin: 0 auto;
  padding: 12px 20px calc(24px + env(safe-area-inset-bottom));
}

.mbr-donate__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 56px;
  border: 0;
  border-radius: var(--mbr-radius-full);
  background: var(--mbr-color-primary);
  box-shadow: 0 8px 20px -4px rgba(var(--mbr-color-primary-rgb), 0.35);
  font-family: var(--mbr-font-headline) !important;
  font-size: 14px;
  font-weight: 700;
  color: var(--mbr-color-on-primary);
  transition: transform 0.15s ease, background-color 0.15s ease;
}

.mbr-donate__cta:hover:not(:disabled) {
  background: var(--mbr-color-primary-light);
}

.mbr-donate__cta:active:not(:disabled) {
  transform: scale(0.98);
}

.mbr-donate__cta:disabled {
  opacity: 0.4;
  box-shadow: none;
}

/* the body-font reset above would otherwise reach the label */
.mbr-donate__cta span {
  font-family: var(--mbr-font-headline);
}

.mbr-donate__cta svg {
  width: 16px;
  height: 16px;
}
</style>
